import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "../../../api/axios";
import { useSelector } from "react-redux";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ProjectAccessDialog } from "./ProjectAccessDialog";
import { ProjectParticipantStatus } from "./utils";
import { projectParticipantsRole } from "./utils";

export const ProjectAccessesSection = ({ projectId, projectPermissions, className = "" }) => {
  const { t } = useTranslation();
  const [projectAccesses, setProjectAccesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccess, setEditingAccess] = useState(null);
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const showSyncStatus = currentCompany?.activeTripletexIntegration;
  
  // Check if user can manage participants
  const canManage = projectPermissions?.canManageParticipants || projectPermissions?.isAdmin || false;

  useEffect(() => {
    if (projectId) {
      fetchProjectAccesses();
    }
  }, [projectId]);

  const fetchProjectAccesses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/projects/${projectId}`, {
        params: { fields: "participants" },
      });
      setProjectAccesses(response.data.participants || []);
    } catch (err) {
      console.error("Error fetching project accesses:", err);
      setError("Failed to load project accesses");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingAccess(null);
    setDialogOpen(true);
  };

  const handleEdit = (access) => {
    setEditingAccess(access);
    setDialogOpen(true);
  };

  const handleDelete = async (accessId) => {
    if (!window.confirm(t("project.participants.delete_confirm"))) {
      return;
    }

    try {
      // Get current project data
      const projectResponse = await axios.get(`/projects/${projectId}`);
      const project = projectResponse.data;

      // Mark the participant for deletion
      const updatedParticipants = (project.participants || []).map((p) =>
        p.id === accessId ? { ...p, delete: true } : p
      );

      // Update project with deleted participant
      await axios.put(`/projects/${projectId}`, {
        project: {
          ...project,
          participants: updatedParticipants,
        },
      });

      // Remove from local state
      setProjectAccesses((prev) => prev.filter((p) => p.id !== accessId));
    } catch (err) {
      console.error("Error deleting project access:", err);
      setError("Failed to remove participant");
    }
  };

  const handleDialogClose = (saved = false) => {
    setDialogOpen(false);
    setEditingAccess(null);
    if (saved) {
      fetchProjectAccesses();
    }
  };

  const handleDialogSuccess = (newAccess) => {
    if (editingAccess) {
      // Update existing access in local state
      setProjectAccesses((prev) =>
        prev.map((p) => (p.id === newAccess.id ? newAccess : p))
      );
    } else {
      // Add new access to local state
      setProjectAccesses((prev) => [...prev, newAccess]);
    }
    setDialogOpen(false);
    setEditingAccess(null);
  };

  if (loading) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="animate-pulse flex space-x-4 w-full">
              <div className="rounded-full bg-blue-200 h-10 w-10"></div>
              <div className="flex-1 space-y-2 py-1">
                <div className="h-4 bg-blue-200 rounded w-1/4"></div>
                <div className="h-3 bg-blue-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
        <div className="bg-red-50 px-6 py-4 border-b border-red-200">
          <div className="flex items-center">
            <div className="text-red-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-red-900">{t("project.participants.error_loading")}</h3>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-gray-900">{t("project.participants.title")}</h3>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                    {projectAccesses.length}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{t("project.participants.description")}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              {canManage && (
                <button
                  onClick={handleAdd}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {t("project.participants.add")}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="bg-white px-6 py-6">
          {projectAccesses.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">{t("project.participants.none")}</p>
              {canManage && (
                <button
                  onClick={handleAdd}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {t("project.participants.add_first")}
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {projectAccesses.map((access) => (
                <div
                  key={access.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t("project.participants.user")}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {access.user ? (
                          <Link
                            to={`/dashboard/users/${access.user.id}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {access.displayTitle || access.user.displayTitle || "-"}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t("project.participants.role")}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {access.role || "-"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t("project.participants.status")}</dt>
                      <dd className="mt-1">
                        <ProjectParticipantStatus participant={access} />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{t("project.participants.admin")}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {access.admin ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            {t("Yes")}
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                            {t("No")}
                          </span>
                        )}
                      </dd>
                    </div>
                  </div>
                  {showSyncStatus && (
                    <div className="ml-4">
                      {access.api_id ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {t("project.synced")}
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          {t("project.not_synced")}
                        </span>
                      )}
                    </div>
                  )}
                  {canManage && (
                    <div className="ml-4 flex space-x-2">
                      <button
                        onClick={() => handleEdit(access)}
                        className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        title={t("project.participants.edit")}
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(access.id)}
                        className="inline-flex items-center px-2 py-1 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        title={t("project.participants.delete")}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ProjectAccessDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        projectId={projectId}
        access={editingAccess}
        onSuccess={handleDialogSuccess}
      />
    </>
  );
};

